/**
 * Вкладка «Лента вакансий» в настройках: источники (вход в Telegram, каналы, Talanto, Himalayas, LinkedIn,
 * Djinni) и общий фильтр.
 */
import {
  FEED_LANG_LABELS,
  FEED_LANGS,
  FEED_LEVEL_LABELS,
  FEED_LEVELS,
  fmtDate,
  FORMAT_LABELS,
  FORMATS,
  type FeedLang,
  type FeedLevel,
  type Format,
} from '@jt/core';
import type { FeedFilterDto, FeedSettings, NOTIFY_EVENTS } from '@jt/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { ApiError, del, get, post, put } from '../api/client';
import { useIsAdmin, useSettings, useSettingsSave } from '../api/hooks';
import { useIntegrations } from '../pages/integrations';
import { ConfirmButton, useUI } from '../components/overlays';
import { Icon } from '../components/ui';

export const feedSettingsKey = ['feed-settings'] as const;
const errText = (x: unknown) =>
  x instanceof ApiError ? (Object.values(x.fields)[0] ?? x.message) : 'Не получилось';
const when = (iso: string | null) => (iso ? `${fmtDate(iso.slice(0, 10))}, ${iso.slice(11, 16)} UTC` : '');

function TelegramLogin({ st }: { st: FeedSettings }) {
  const ui = useUI();
  const qc = useQueryClient();
  const admin = useIsAdmin();
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const t = st.telegram;
  const send = async (path: string, body: object) => {
    setBusy(true);
    setErr('');
    try {
      qc.setQueryData(feedSettingsKey, await post<FeedSettings>(path, body));
      setValue('');
    } catch (x) {
      setErr(errText(x));
    } finally {
      setBusy(false);
    }
  };
  if (!t.available)
    return (
      <p className="muted sm">
        {admin
          ? 'Чтобы читать каналы, задайте api_id и api_hash приложения Telegram: «Подключения» → Telegram-бот.'
          : 'Чтение Telegram-каналов ещё не настроено администратором.'}
      </p>
    );
  if (t.connected)
    return (
      <div className="btnrow">
        <span className="sm" style={{ alignSelf: 'center' }}>
          Вход в Telegram: <b>{t.name}</b>
        </span>
        <ConfirmButton
          className="btn sm ghost danger"
          confirmText="Выйти из Telegram в трекере?"
          onConfirm={async () => {
            await del('/feed/telegram');
            await qc.invalidateQueries({ queryKey: feedSettingsKey });
            ui.toast('Трекер больше не читает Telegram');
          }}
        >
          Выйти
        </ConfirmButton>
      </div>
    );
  const step = t.pending ?? 'phone';
  const label =
    step === 'phone'
      ? 'Номер телефона Telegram'
      : step === 'code'
        ? 'Код из Telegram'
        : 'Пароль двухэтапной проверки';
  return (
    <form
      className="secret"
      onSubmit={(e) => {
        e.preventDefault();
        if (!value.trim()) return;
        if (step === 'phone') void send('/feed/telegram/start', { phone: value });
        else if (step === 'code') void send('/feed/telegram/code', { code: value });
        else void send('/feed/telegram/password', { password: value });
      }}
    >
      <div className="secret-h">
        <label htmlFor="tg-login">{label}</label>
        <span className="sm muted">шаг {step === 'phone' ? 1 : step === 'code' ? 2 : 3}</span>
      </div>
      <p className="muted sm" style={{ margin: '0 0 6px' }}>
        {step === 'phone'
          ? 'Трекер будет читать каналы из списка от вашего имени — так же, как приложение Telegram на компьютере. Писать от вашего имени он не будет.'
          : step === 'code'
            ? 'Telegram прислал код в приложение (сообщение от «Telegram»). Введите его здесь.'
            : `Включена двухэтапная проверка.${t.hint ? ` Подсказка: ${t.hint}.` : ''} Пароль сразу уходит в Telegram и в трекере не сохраняется.`}
      </p>
      <div className="secret-row">
        <input
          id="tg-login"
          type={step === 'password' ? 'password' : step === 'code' ? 'text' : 'tel'}
          inputMode={step === 'code' ? 'numeric' : undefined}
          autoComplete={step === 'password' ? 'current-password' : step === 'code' ? 'one-time-code' : 'tel'}
          value={value}
          placeholder={step === 'phone' ? '+34 600 000 000' : step === 'code' ? '12345' : ''}
          aria-invalid={err ? true : undefined}
          onChange={(e) => setValue(e.target.value)}
        />
        <button className="btn primary" type="submit" disabled={busy || !value.trim()}>
          {busy ? 'Отправляю…' : step === 'phone' ? 'Получить код' : 'Войти'}
        </button>
        {step !== 'phone' && (
          <button
            className="btn ghost"
            type="button"
            onClick={async () => {
              await del('/feed/telegram');
              await qc.invalidateQueries({ queryKey: feedSettingsKey });
            }}
          >
            Начать заново
          </button>
        )}
      </div>
      {(err || t.lastError) && (
        <p className="sm" role="alert" style={{ color: 'var(--danger)', margin: '6px 0 0' }}>
          {err || t.lastError}
        </p>
      )}
    </form>
  );
}

function Channels({ st }: { st: FeedSettings }) {
  const ui = useUI();
  const qc = useQueryClient();
  const [text, setText] = useState(st.channels.map((c) => c.ref).join('\n'));
  const [busy, setBusy] = useState(false);
  useEffect(() => setText(st.channels.map((c) => c.ref).join('\n')), [st.channels]);
  return (
    <div className="secret">
      <div className="secret-h">
        <label htmlFor="feed-channels">Каналы Telegram</label>
        <span className="sm muted">{st.channels.length} шт.</span>
      </div>
      <textarea
        id="feed-channels"
        rows={5}
        value={text}
        placeholder={'@qa_jobs\nhttps://t.me/remote_it\nhttps://t.me/+приглашение'}
        onChange={(e) => setText(e.target.value)}
      />
      <p className="muted sm" style={{ margin: '4px 0 6px' }}>
        По одному на строку: @имя, ссылка t.me/… или ссылка-приглашение закрытого канала (вы должны быть его
        участником).
      </p>
      <button
        className="btn primary"
        type="button"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            const refs = text
              .split(/\n|,/)
              .map((x) => x.trim())
              .filter(Boolean);
            qc.setQueryData(feedSettingsKey, await put<FeedSettings>('/feed/channels', { refs }));
            ui.toast('Каналы сохранены — проверяю');
          } catch (x) {
            ui.toast(errText(x));
          } finally {
            setBusy(false);
          }
        }}
      >
        Сохранить каналы
      </button>
      {st.channels.some((c) => c.lastCheckedAt || c.lastError) && (
        <ul className="cdet" style={{ marginTop: 10 }}>
          {st.channels.map((c) => (
            <li key={c.id}>
              <span
                className={`sdot ${c.lastError ? 'bad' : c.lastCheckedAt ? 'ok' : 'off'}`}
                aria-hidden="true"
              />
              <span style={{ color: 'var(--ink)' }}>{c.title || c.ref}</span>
              <span className="muted sm">
                {c.lastError
                  ? c.lastError
                  : c.lastCheckedAt
                    ? `проверен ${when(c.lastCheckedAt)}`
                    : 'ещё не проверялся'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const lines = (s: string) => [
  ...new Set(
    s
      .split(/\n|,/)
      .map((x) => x.trim())
      .filter(Boolean),
  ),
];

type SaveFilter = (next: FeedFilterDto) => Promise<void>;

/** Строка источника: переключатель (или точка состояния), название, как часто, а ниже — его параметры. */
function Source({
  name,
  meta,
  on,
  onToggle,
  health,
  children,
}: {
  name: string;
  meta: ReactNode;
  on: boolean;
  /** нет — источник включается не здесь (Telegram — списком каналов, Talanto — подключением) */
  onToggle?: (on: boolean) => void;
  health?: 'ok' | 'bad' | 'off';
  children?: ReactNode;
}) {
  return (
    <div className={`src${on ? ' on' : ''}`}>
      <div className="src-h">
        {onToggle ? (
          <input
            type="checkbox"
            className="switch"
            role="switch"
            checked={on}
            aria-label={name}
            onChange={(e) => onToggle(e.target.checked)}
          />
        ) : (
          <span className={`sdot ${health ?? (on ? 'ok' : 'off')}`} aria-hidden="true" />
        )}
        <div className="lm">
          <b>{name}</b>
          <div className="meta">{meta}</div>
        </div>
      </div>
      {on && children && <div className="src-b">{children}</div>}
    </div>
  );
}

function Sources({ st, f, save }: { st: FeedSettings; f: FeedFilterDto; save: SaveFilter }) {
  const talanto = useQuery({
    queryKey: ['feed-talanto-filters'],
    queryFn: () => get<{ id: string; name: string }[]>('/feed/talanto-filters'),
    enabled: st.talanto.connected,
  });
  const tgOn = st.telegram.connected && st.channels.length > 0;
  const tgBad = st.channels.some((c) => c.lastError) || !!st.telegram.lastError;
  return (
    <section className="box" id="feed">
      <div className="boxh">
        <h2>Источники вакансий</h2>
        <ScanNow st={st} />
      </div>
      <p className="muted sm" style={{ marginTop: -6 }}>
        Новое из источников сначала отбирается по словам фильтра (бесплатно), затем быстрая модель Claude
        достаёт компанию, роль, зарплату и формат и отсеивает неподходящее. Результат — в разделе «Вакансии».
      </p>
      <div className="srcs">
        <Source
          name="Telegram-каналы"
          on
          health={tgBad ? 'bad' : tgOn ? 'ok' : 'off'}
          meta={
            st.telegram.connected
              ? `вход: ${st.telegram.name} · каналов: ${st.channels.length} · раз в 10 минут`
              : 'посты каналов, раз в 10 минут · нужен вход в Telegram'
          }
        >
          <TelegramLogin st={st} />
          <Channels st={st} />
        </Source>
        <Source
          name="Talanto"
          on={st.talanto.connected}
          meta={
            st.talanto.connected ? (
              'сохранённый фильтр Talanto, раз в 10 минут'
            ) : (
              <>
                не подключён — <a href="#talanto">подключить</a>
              </>
            )
          }
        >
          <label className="f">
            Фильтр Talanto для ленты
            <select
              value={f.talantoFilterId ?? ''}
              onChange={(e) => void save({ ...f, talantoFilterId: e.target.value || null })}
            >
              <option value="">Первый сохранённый</option>
              {(talanto.data ?? []).map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </label>
        </Source>
        <Source
          name="Himalayas"
          meta="удалённая работа, раз в 3 часа"
          on={f.himalayas}
          onToggle={(on) => void save({ ...f, himalayas: on })}
        >
          <label className="f">
            Работа из страны (код из 2 букв; пусто — только «из любой страны»)
            <input
              defaultValue={f.himalayasCountry}
              maxLength={2}
              placeholder="ES"
              style={{ textTransform: 'uppercase' }}
              onBlur={(e) => {
                const v = e.target.value.trim().toUpperCase();
                if (/^([A-Z]{2})?$/.test(v) && v !== f.himalayasCountry)
                  void save({ ...f, himalayasCountry: v });
              }}
            />
          </label>
        </Source>
        <Source
          name="LinkedIn — поиск"
          meta="публичный поиск без входа в аккаунт, раз в 2 часа"
          on={f.linkedin}
          onToggle={(on) => void save({ ...f, linkedin: on })}
        >
          <label className="f">
            Где искать (страна или город)
            <input
              defaultValue={f.linkedinLocation}
              placeholder="Spain"
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v && v !== f.linkedinLocation) void save({ ...f, linkedinLocation: v });
              }}
            />
          </label>
        </Source>
        <Source
          name="LinkedIn — подборки из почты"
          meta="письма «Оповещения о вакансиях» из Gmail; что присылать — настраивается в LinkedIn"
          on={f.linkedinAlerts}
          onToggle={(on) => void save({ ...f, linkedinAlerts: on })}
        />
        <Source
          name="Djinni"
          meta="QA с полной удалёнкой, раз в час · разбирает Claude, «только для Украины» отсеивается"
          on={f.djinni}
          onToggle={(on) => void save({ ...f, djinni: on })}
        />
      </div>
    </section>
  );
}

function ScanNow({ st }: { st: FeedSettings }) {
  const ui = useUI();
  return (
    <div className="btnrow" style={{ justifyContent: 'flex-end' }}>
      <span className="muted sm" style={{ alignSelf: 'center' }}>
        {st.lastScanAt ? `проверено ${when(st.lastScanAt)}` : 'ещё не проверялось'}
      </span>
      <button
        className="btn sm"
        type="button"
        onClick={async () => {
          try {
            await post('/feed/scan');
            ui.toast('Проверяю — новые вакансии появятся в разделе «Вакансии»');
          } catch (x) {
            ui.toast(errText(x));
          }
        }}
      >
        <Icon name="restore" size={15} /> Проверить сейчас
      </button>
    </div>
  );
}

function Filter({ st, f, save }: { st: FeedSettings; f: FeedFilterDto; save: SaveFilter }) {
  const [kw, setKw] = useState(st.filter.keywords.join(', '));
  const [ex, setEx] = useState(st.filter.exclude.join(', '));
  const toggle = <T,>(list: T[], v: T) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  return (
    <section className="box" id="feed-filter">
      <div className="boxh">
        <h2>Фильтр</h2>
        <span className="sm muted">сохраняется сразу · общий для всех источников</span>
      </div>
      <div className="grid">
        <label className="f full">
          Ищем слова (хотя бы одно)
          <input
            value={kw}
            onChange={(e) => setKw(e.target.value)}
            onBlur={() => void save({ ...f, keywords: lines(kw) })}
          />
        </label>
        <label className="f full">
          Исключить, если есть
          <input
            value={ex}
            onChange={(e) => setEx(e.target.value)}
            onBlur={() => void save({ ...f, exclude: lines(ex) })}
          />
        </label>
        <div className="f">
          Уровень
          <div className="days chips" role="group" aria-label="Уровень">
            {FEED_LEVELS.map((l) => (
              <button
                key={l}
                type="button"
                aria-pressed={f.levels.includes(l)}
                onClick={() => void save({ ...f, levels: toggle(f.levels, l as FeedLevel) })}
              >
                {FEED_LEVEL_LABELS[l]}
              </button>
            ))}
          </div>
        </div>
        <div className="f">
          Формат
          <div className="days chips" role="group" aria-label="Формат">
            {FORMATS.map((x) => (
              <button
                key={x}
                type="button"
                aria-pressed={f.formats.includes(x)}
                onClick={() => void save({ ...f, formats: toggle(f.formats, x as Format) })}
              >
                {FORMAT_LABELS[x]}
              </button>
            ))}
          </div>
        </div>
        <div className="f full">
          Язык объявления
          <div className="days chips" role="group" aria-label="Язык объявления">
            {FEED_LANGS.map((l) => (
              <button
                key={l}
                type="button"
                aria-pressed={f.languages.includes(l)}
                onClick={() => void save({ ...f, languages: toggle(f.languages, l as FeedLang) })}
              >
                {FEED_LANG_LABELS[l]}
              </button>
            ))}
          </div>
          <span className="muted sm">
            Ничего не выбрано — любой язык. Язык определяется по тексту; короткие объявления, где его не
            понять, проходят.
          </span>
        </div>
        <label className="f">
          Зарплата от, $ в месяц (пусто — любая)
          <input
            inputMode="numeric"
            defaultValue={f.salaryMinUsd ?? ''}
            onBlur={(e) => {
              const v = e.target.value.replace(/[^\d]/g, '');
              void save({ ...f, salaryMinUsd: v ? Number(v) : null });
            }}
          />
        </label>
        <label
          className="f"
          style={{ flexDirection: 'row', alignItems: 'center', gap: 10, color: 'var(--ink)' }}
        >
          <input
            type="checkbox"
            checked={f.includeNoSalary}
            onChange={(e) => void save({ ...f, includeNoSalary: e.target.checked })}
          />
          Показывать вакансии без зарплаты
        </label>
      </div>
    </section>
  );
}

/**
 * Новые вакансии — в Telegram карточкой с кнопками. Та же настройка, что «Новые вакансии из ленты»
 * в «Подключения» → Telegram-бот → «Какие события присылать».
 */
function TelegramNotify() {
  const ui = useUI();
  const s = useSettings().data;
  const save = useSettingsSave();
  const tg = useIntegrations().data?.telegram;
  if (!s) return null;
  const on = s.notifyEvents.includes('feed');
  return (
    <section className="box">
      <div className="src-h">
        <input
          type="checkbox"
          className="switch"
          role="switch"
          checked={on}
          disabled={!tg?.linked}
          aria-label="Присылать новые вакансии в Telegram"
          onChange={(e) =>
            void save({
              notifyEvents: (e.target.checked
                ? [...new Set([...s.notifyEvents, 'feed'])]
                : s.notifyEvents.filter((x) => x !== 'feed')) as (typeof NOTIFY_EVENTS)[number][],
            }).catch(() => ui.toast('Не сохранилось. Повторите.'))
          }
        />
        <div className="lm">
          <b>Присылать новые вакансии в Telegram</b>
          <div className="meta">
            {tg?.linked ? (
              'Каждая новая вакансия ленты — сообщением в бот: роль, компания, зарплата, источник и кнопки «В мои отклики», «Скрыть», «Открыть в трекере». Проверка — раз в 5 минут.'
            ) : (
              <>
                Сначала привяжите Telegram-бота: <a href="#telegram">«Подключения» → Telegram-бот</a>.
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/** Вкладка «Лента вакансий»: источники со своими переключателями и общий фильтр. */
export function FeedTab() {
  const ui = useUI();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: feedSettingsKey, queryFn: () => get<FeedSettings>('/feed/settings') });
  const [f, setF] = useState<FeedFilterDto | null>(null);
  const st = q.data;
  if (!st) return null;
  const filter = f ?? st.filter;
  const save: SaveFilter = async (next) => {
    setF(next);
    try {
      qc.setQueryData(feedSettingsKey, await put<FeedSettings>('/feed/filter', next));
    } catch (x) {
      ui.toast(errText(x));
    }
  };
  return (
    <>
      <TelegramNotify />
      <Sources st={st} f={filter} save={save} />
      <Filter st={st} f={filter} save={save} />
    </>
  );
}
